import Component from '@glimmer/component';
import Button from './button';
import Popover from './popover';
import type { ButtonIntent, ButtonAppearance, ButtonSize } from './button';
import type { PopoverPlacement } from './popover';
import type { SafeString } from '@ember/template';
import type { ComponentLike } from '@glint/template';

/**
 * Full component signature for type-only consumers.
 */
export interface TooltipButtonSignature {
  Args: {
    /** The tooltip text to display on hover */
    tooltip: string | SafeString;
    /** Tooltip placement relative to button @default "top" */
    tooltipPlacement?: PopoverPlacement;
    /** Whether the tooltip is currently disabled (e.g., when a drawer is open) */
    tooltipDisabled?: boolean;
    /** HTML button type */
    type?: 'button' | 'submit' | 'reset';
    /** Visual intent */
    intent?: ButtonIntent;
    /** Appearance variant */
    appearance?: ButtonAppearance;
    /** Size */
    size?: ButtonSize;
    /** Icon component (Phosphor icon etc.) */
    icon?: ComponentLike<{ Args: { size?: number | string }; Element: SVGElement }>;
    /** Icon size override */
    iconSize?: number;
    /** Text label */
    label?: string;
    /** Icon placement */
    iconPlacement?: 'start' | 'end';
    /** Whether button is in active/selected state (shows highlighted background) */
    isActive?: boolean;
    /** Disabled state */
    isDisabled?: boolean;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLButtonElement;
}

/**
 * TooltipButton – A button with an integrated hover tooltip.
 *
 * Composes the reimagined Button and Popover components. The tooltip appears
 * on hover via Popover's `@triggerMode="hover"`, with smart viewport-aware
 * positioning and smooth spring-feel animation.
 *
 * Features an `@isActive` state for toolbar/drawer toggle patterns that
 * highlights the button with a tinted background.
 *
 * @example Basic icon-only with tooltip
 * ```gts
 * import TooltipButton from 'tio-ui/components/tooltip-button';
 * import { PhFloppyDisk } from 'ember-phosphor-icons';
 *
 * <TooltipButton
 *   @tooltip="Save changes"
 *   @icon={{PhFloppyDisk}}
 *   @appearance="minimal"
 *   {{on "click" this.save}}
 * />
 * ```
 *
 * @example With active state (drawer toggle)
 * ```gts
 * <TooltipButton
 *   @tooltip="Comments"
 *   @isActive={{this.isDrawerOpen}}
 *   @appearance="minimal"
 *   @icon={{PhChat}}
 *   {{on "click" this.toggleDrawer}}
 * />
 * ```
 *
 * @example With label and intent
 * ```gts
 * <TooltipButton
 *   @tooltip="This will permanently remove the item"
 *   @intent="danger"
 *   @icon={{PhTrash}}
 *   @label="Delete"
 *   {{on "click" this.delete}}
 * />
 * ```
 */
export default class TooltipButton extends Component<TooltipButtonSignature> {
  private _hoverCloseTimer: ReturnType<typeof setTimeout> | undefined;

  get placement(): PopoverPlacement {
    return this.args.tooltipPlacement ?? 'top';
  }

  /**
   * Active-state background tint when `@isActive={{true}}`. Each intent
   * maps to a named `.tio-tooltip-btn--active-*` recipe in
   * `addons/tio-ui/src/styles/` that pulls its color from a
   * mode-aware semantic token (status `--*-bg`, sunken bg, etc.) —
   * dark mode cascades for free, no per-class dark override.
   */
  get activeClasses(): string {
    if (!this.args.isActive) return '';

    const intent = this.args.intent ?? 'default';
    switch (intent) {
      case 'primary':
        return 'tio-tooltip-btn--active-primary';
      case 'danger':
        return 'tio-tooltip-btn--active-danger';
      case 'warning':
        return 'tio-tooltip-btn--active-warning';
      case 'success':
        return 'tio-tooltip-btn--active-success';
      default:
        return 'tio-tooltip-btn--active-neutral';
    }
  }

  // Close the tooltip on click so it doesn't obscure content
  handleClick = (popoverClose: () => void) => {
    popoverClose();
  };

  handleTriggerEnter = (popoverOpen: () => void) => {
    if (this._hoverCloseTimer) {
      clearTimeout(this._hoverCloseTimer);
      this._hoverCloseTimer = undefined;
    }
    popoverOpen();
  };

  handleTriggerLeave = (popoverClose: () => void) => {
    this._hoverCloseTimer = setTimeout(() => popoverClose(), 150);
  };

  get ariaLabelText(): string {
    return String(this.args.tooltip);
  }

  <template>
    {{#if @tooltipDisabled}}
      <Button
        @type={{@type}}
        @intent={{@intent}}
        @appearance={{@appearance}}
        @size={{@size}}
        @icon={{@icon}}
        @iconSize={{@iconSize}}
        @label={{@label}}
        @iconPlacement={{@iconPlacement}}
        @isDisabled={{@isDisabled}}
        @ariaLabel={{this.ariaLabelText}}
        class={{this.activeClasses}}
        data-test-tooltip-button
        ...attributes
      >
        {{yield}}
      </Button>
    {{else}}
      <Popover
        @placement={{this.placement}}
        @triggerMode="hover"
        @maxWidth={{280}}
        data-test-tooltip-button-popover
      >
        <:trigger as |t|>
          <span
            data-test-tooltip-button-trigger
            class="inline-flex"
            {{t.register}}
            {{on "mouseenter" (fn this.handleTriggerEnter t.open)}}
            {{on "mouseleave" (fn this.handleTriggerLeave t.close)}}
            {{on "focusin" (fn this.handleTriggerEnter t.open)}}
            {{on "focusout" (fn this.handleTriggerLeave t.close)}}
          >
            <Button
              @type={{@type}}
              @intent={{@intent}}
              @appearance={{@appearance}}
              @size={{@size}}
              @icon={{@icon}}
              @iconSize={{@iconSize}}
              @label={{@label}}
              @iconPlacement={{@iconPlacement}}
              @isDisabled={{@isDisabled}}
              @ariaLabel={{this.ariaLabelText}}
              class={{this.activeClasses}}
              {{on "click" (fn this.handleClick t.close)}}
              data-test-tooltip-button
              ...attributes
            >
              {{yield}}
            </Button>
          </span>
        </:trigger>
        <:body>
          {{@tooltip}}
        </:body>
      </Popover>
    {{/if}}
  </template>
}
